import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { HttpError, createHolding, sellHolding } from '../lib/api.ts';
import {
  fmtHKD,
  fmtPctSigned,
  fmtSignedHKD,
  fmtUSD,
  labelForSignal,
  todayLocalISO,
  usdToHkdDisplay,
} from '../lib/format.ts';
import type { FxRate } from '../lib/types.ts';
import type { CardDetail } from '../lib/types.ts';
import { Icon } from './Icon.tsx';

interface TradeModalShellProps {
  title: string;
  eyebrow: string;
  sub: React.ReactNode;
  tone: 'buy' | 'sell';
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}

function TradeModalShell({
  title,
  eyebrow,
  sub,
  tone,
  onClose,
  children,
  footer,
}: TradeModalShellProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: scrim — Escape closes via global handler */}
      {/* biome-ignore lint/a11y/useSemanticElements: scrim is decorative; close-btn handles keyboard */}
      <div className="scrim" onClick={onClose} role="presentation" />
      <div
        className={`trade-modal ${tone === 'sell' ? 'sell' : ''}`}
        // biome-ignore lint/a11y/useSemanticElements: native <dialog> needs imperative showModal; div role=dialog matches existing card modal pattern.
        role="dialog"
        aria-label={eyebrow}
      >
        <div className="trade-head">
          <div>
            <div className="trade-eyebrow mono">{eyebrow}</div>
            <div className="trade-title jp">{title}</div>
            <div className="trade-sub mono">{sub}</div>
          </div>
          <button type="button" className="close-btn" aria-label="Cancel" onClick={onClose}>
            <Icon name="close" size={12} />
          </button>
        </div>
        <div className="trade-body">{children}</div>
        <div className="trade-foot">{footer}</div>
      </div>
    </>
  );
}

function parseHkdInput(s: string): number {
  // Accept "1,234.56" or "1234" — strip commas + spaces. NaN if not parseable.
  const cleaned = s.replace(/[, ]/g, '');
  if (cleaned === '') return Number.NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : Number.NaN;
}

interface BuyModalProps {
  card: CardDetail;
  fx: FxRate;
  onClose: () => void;
  onSuccess?: (holdingId: string) => void;
  /** Initial mode. Defaults to 'real'. Operator can flip it inside the modal. */
  initialMode?: 'real' | 'paper';
}

export function BuyModal({ card, fx, onClose, onSuccess, initialMode = 'real' }: BuyModalProps) {
  const suggested = card.price == null ? 0 : Math.round(usdToHkdDisplay(card.price, fx));
  const [priceStr, setPriceStr] = useState(suggested ? suggested.toLocaleString('en-US') : '');
  const [date, setDate] = useState(todayLocalISO());
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState<'real' | 'paper'>(initialMode);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  const price = parseHkdInput(priceStr);
  const validPrice = Number.isFinite(price) && price > 0 && price <= 10_000_000;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const cost = validPrice ? price * qty : 0;

  const mut = useMutation({
    mutationFn: () =>
      createHolding({
        card_id: card.id,
        qty,
        buy_hkd: price,
        buy_at: date,
        mode,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['holdings'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['card', card.id] });
      onSuccess?.(r.id);
      onClose();
    },
    onError: (e) => {
      if (e instanceof HttpError) {
        if (e.status === 404) setErr('That card is not in the active list.');
        else if (e.status === 400) setErr('Check the price, date, and quantity.');
        else if (e.status === 401) setErr('Session expired. Reload and sign in.');
        else setErr(`Could not save (${e.status}).`);
      } else setErr('Network error.');
    },
  });

  return (
    <TradeModalShell
      eyebrow={mode === 'paper' ? 'Paper buy · what-if entry' : 'Buy · enter purchase'}
      title={card.jpEn ?? card.jp}
      sub={`${card.set} · ${card.num} · ${card.rarity}`}
      tone="buy"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-buy"
            disabled={!validPrice || !validDate || mut.isPending}
            onClick={() => {
              setErr(null);
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Saving…' : mode === 'paper' ? 'Confirm paper buy' : 'Confirm buy'}
          </button>
        </>
      }
    >
      <div className="trade-field">
        <span className="trade-field-label">Mode</span>
        <div className="port-mode">
          <button
            type="button"
            aria-pressed={mode === 'real'}
            className={mode === 'real' ? 'on' : ''}
            onClick={() => setMode('real')}
          >
            Real
          </button>
          <button
            type="button"
            aria-pressed={mode === 'paper'}
            className={mode === 'paper' ? 'on' : ''}
            onClick={() => setMode('paper')}
          >
            Paper
          </button>
        </div>
        <div className="trade-hint mono">
          {mode === 'paper'
            ? 'No real money tracked — Holdings → Paper tab shows P&L vs live prices.'
            : 'Recorded as a real purchase — appears on the tile stripe and your position block.'}
        </div>
      </div>

      <div className="trade-field">
        <label htmlFor="buy-price">Price paid</label>
        <div className="ccy-input">
          <span className="ccy-pre">HK$</span>
          <input
            id="buy-price"
            className="trade-input mono"
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            inputMode="decimal"
          />
          {validPrice && <span className="ccy-equiv">≈ ${fmtUSD(price / fx.usd_hkd)} USD</span>}
        </div>
        {card.price != null && (
          <div className="trade-hint mono">
            Last sale HK${fmtHKD(usdToHkdDisplay(card.price, fx))} · suggested
          </div>
        )}
      </div>

      <div className="trade-field-row">
        <div className="trade-field">
          <label htmlFor="buy-date">Date</label>
          <input
            id="buy-date"
            className="trade-input mono"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayLocalISO()}
          />
          <div className="trade-hint mono">Today by default</div>
        </div>
        <div className="trade-field" style={{ maxWidth: 130 }}>
          <label htmlFor="buy-qty">Quantity</label>
          <div className="qty-stepper">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className="mono">{qty}</span>
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(999, q + 1))}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {validPrice && (
        <div className="trade-summary">
          <div className="row">
            <span>Cost basis</span>
            <span className="mono">HK${fmtHKD(cost)}</span>
          </div>
          <div className="row">
            <span>USD equivalent</span>
            <span className="mono" style={{ color: 'var(--ink-dim)' }}>
              ${fmtUSD(cost / fx.usd_hkd)}
            </span>
          </div>
          <div className="row">
            <span>Signal at purchase</span>
            <span className={`mono sig-tone-${card.signal}`}>
              {labelForSignal(card.signal)} {card.score}/5
            </span>
          </div>
        </div>
      )}

      {err && <div className="trade-error mono">{err}</div>}
    </TradeModalShell>
  );
}

interface SellModalProps {
  card: CardDetail;
  fx: FxRate;
  onClose: () => void;
  /** Specific holding to close. If omitted, server treats earliest open as target. */
  holdingId: string;
  buyHkd: number;
  qty: number;
  daysHeld: number;
  onSuccess?: () => void;
}

export function SellModal({
  card,
  fx,
  onClose,
  holdingId,
  buyHkd,
  qty,
  daysHeld,
  onSuccess,
}: SellModalProps) {
  const suggested = card.price == null ? 0 : Math.round(usdToHkdDisplay(card.price, fx));
  const [priceStr, setPriceStr] = useState(suggested ? suggested.toLocaleString('en-US') : '');
  const [date, setDate] = useState(todayLocalISO());
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();

  const price = parseHkdInput(priceStr);
  const validPrice = Number.isFinite(price) && price > 0 && price <= 10_000_000;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);

  const pnlPerUnit = validPrice ? price - buyHkd : 0;
  const pnlTotal = pnlPerUnit * qty;
  const pnlPct = buyHkd > 0 ? (pnlPerUnit / buyHkd) * 100 : 0;
  const pnlPos = pnlTotal >= 0;

  const mut = useMutation({
    mutationFn: () => sellHolding(holdingId, { sell_hkd: price, sell_at: date }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holdings'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['card', card.id] });
      onSuccess?.();
      onClose();
    },
    onError: (e) => {
      if (e instanceof HttpError) {
        if (e.status === 409) setErr('Already closed. Refresh and try again.');
        else if (e.status === 404) setErr('Holding not found.');
        else if (e.status === 400) setErr('Check the price and date.');
        else if (e.status === 401) setErr('Session expired. Reload and sign in.');
        else setErr(`Could not sell (${e.status}).`);
      } else setErr('Network error.');
    },
  });

  return (
    <TradeModalShell
      eyebrow="Sell · enter sale"
      title={card.jpEn ?? card.jp}
      sub={`held ${daysHeld}d · bought HK$${fmtHKD(buyHkd)} · ×${qty}`}
      tone="sell"
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn-primary ${pnlPos ? 'btn-buy' : 'btn-sell'}`}
            disabled={!validPrice || !validDate || mut.isPending}
            onClick={() => {
              setErr(null);
              mut.mutate();
            }}
          >
            {mut.isPending ? 'Saving…' : 'Confirm sell'}
          </button>
        </>
      }
    >
      <div className="trade-field">
        <label htmlFor="sell-price">Sale price (per unit)</label>
        <div className="ccy-input">
          <span className="ccy-pre">HK$</span>
          <input
            id="sell-price"
            className="trade-input mono"
            value={priceStr}
            onChange={(e) => setPriceStr(e.target.value)}
            inputMode="decimal"
          />
          {validPrice && <span className="ccy-equiv">≈ ${fmtUSD(price / fx.usd_hkd)} USD</span>}
        </div>
        {card.price != null && (
          <div className="trade-hint mono">Last sale HK${fmtHKD(usdToHkdDisplay(card.price, fx))}</div>
        )}
      </div>

      <div className="trade-field">
        <label htmlFor="sell-date">Date</label>
        <input
          id="sell-date"
          className="trade-input mono"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={todayLocalISO()}
        />
        <div className="trade-hint mono">Today by default</div>
      </div>

      {validPrice && (
        <div className={`trade-pnl ${pnlPos ? 'pos' : 'neg'}`}>
          <div className="trade-pnl-head mono">Realized P&L preview</div>
          <div className="trade-pnl-big mono">
            {fmtSignedHKD(pnlTotal)}
            <span className="trade-pnl-pct">{fmtPctSigned(pnlPct)}</span>
          </div>
          <div className="trade-pnl-meta mono">
            HK${fmtHKD(buyHkd * qty)} → HK${fmtHKD(price * qty)} · {daysHeld}d
          </div>
        </div>
      )}

      {err && <div className="trade-error mono">{err}</div>}
    </TradeModalShell>
  );
}
